import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { searchClient } from '../../../../services/searchConfig';
import { algoliaConfig } from '../../../../config';
import { DEFAULT_SEARCH_DEBOUNCE_MS } from '#types/System.Parameters.types';
import type { FormVersionWithPath } from '#types';

interface AlgoliaFormVersionHit {
  objectID: string;
  _documentPath?: string;
  [key: string]: unknown;
}

const SEARCH_HITS_PER_PAGE = 200;

interface UseHomePageSearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: FormVersionWithPath[];
  isSearching: boolean;
  isSearchActive: boolean;
  resultCount: number;
  searchError: string | null;
  clearSearch: () => void;
}

export const useHomePageSearch = (): UseHomePageSearchReturn => {
  const [algoliaHits, setAlgoliaHits] = useState<AlgoliaFormVersionHit[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [currentQuery, setCurrentQuery] = useState('');

  const latestRequestSeqRef = useRef(0);

  const executeSearch = useCallback(async (query: string): Promise<AlgoliaFormVersionHit[]> => {
    if (!query.trim() || !algoliaConfig?.allIndexName || !searchClient) {
      return [];
    }

    const { results } = await searchClient.search<AlgoliaFormVersionHit>({
      requests: [{
        indexName: algoliaConfig.allIndexName,
        query,
        filters: '_collectionName:formVersions',
        hitsPerPage: SEARCH_HITS_PER_PAGE,
      }],
    });

    const firstResult = results[0];
    if ('hits' in firstResult) {
      return firstResult.hits;
    }

    return [];
  }, []);

  const debouncedSearch = useMemo(() =>
    debounce(async (query: string, seq: number) => {
      if (!query.trim()) {
        if (latestRequestSeqRef.current === seq) {
          setAlgoliaHits([]);
          setIsSearching(false);
        }
        return;
      }

      try {
        const hits = await executeSearch(query);
        if (latestRequestSeqRef.current === seq) {
          setAlgoliaHits(hits);
          setSearchError(null);
        }
      } catch (error) {
        if (latestRequestSeqRef.current === seq) {
          console.warn('[useHomePageSearch] Algolia search failed:', error);
          setSearchError(error instanceof Error ? error.message : 'Search failed');
        }
      } finally {
        if (latestRequestSeqRef.current === seq) {
          setIsSearching(false);
        }
      }
    }, DEFAULT_SEARCH_DEBOUNCE_MS),
  [executeSearch]);

  const setSearchQuery = useCallback((query: string) => {
    setCurrentQuery(query);

    if (!query.trim()) {
      latestRequestSeqRef.current += 1;
      setAlgoliaHits([]);
      setIsSearching(false);
      debouncedSearch.cancel();
      return;
    }

    const seq = latestRequestSeqRef.current + 1;
    latestRequestSeqRef.current = seq;
    setIsSearching(true);
    debouncedSearch(query, seq);
  }, [debouncedSearch]);

  const clearSearch = useCallback(() => {
    setCurrentQuery('');
    latestRequestSeqRef.current += 1;
    setAlgoliaHits([]);
    setIsSearching(false);
    setSearchError(null);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    return () => {
      latestRequestSeqRef.current += 1;
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const searchResults = useMemo((): FormVersionWithPath[] => {
    if (!currentQuery.trim()) return [];

    return algoliaHits.map((hit) => {
      const { objectID, _documentPath, ...rest } = hit;
      return {
        ...rest,
        docId: objectID,
        _documentPath: _documentPath ?? '',
      } as FormVersionWithPath;
    });
  }, [currentQuery, algoliaHits]);

  const isSearchActive = currentQuery.trim().length > 0;

  return {
    searchQuery: currentQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    isSearchActive,
    resultCount: searchResults.length,
    searchError,
    clearSearch,
  };
};
